import { type ComponentProps, type ForwardRefExoticComponent, type RefAttributes, useMemo } from 'react'

import { Label } from '@/components/ui/label.tsx'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx'
import { ChevronDown } from 'lucide-react'

import { EditableComponent } from '@/components/EditableComponent.tsx'
import { DEFAULT_STORY } from '@/constants/DEFAULT_VALUES.ts'
import { STORY_POINTS, mapStoryPoints } from '@/constants/STORY_POINTS.ts'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { useMutateStory } from '@/requests/mutations/useMutateStory.ts'
import { isPointAssignable } from '@/utils/story.ts'

type Props = {
  storyId: string
  value: StoryPoints
}

export const Estimate = ({ storyId, value }: Props) => {
  return (
    <>
      <label htmlFor={`estimate-${storyId}`}>Points</label>
      <span id={`estimate-${storyId}`}>{value}</span>
    </>
  )
}

type EditableVersionProps = {
  storyId: string
  value?: number
  storyType: StoryType
  status: StoryStatus
}

export const EditableEstimate = ({
  storyId,
  value = DEFAULT_STORY().estimate,
  storyType,
  status,
}: EditableVersionProps) => {
  const { mutate } = useMutateStory(storyId)

  const possiblePointsList = useMemo(
    () =>
      isPointAssignable(storyType) && status !== STORY_STATUSES.UNSCHEDULED
        ? Object.entries(STORY_POINTS).filter(([_, v]) => v !== -1)
        : Object.entries(STORY_POINTS),
    [storyType, status],
  )

  const getIcon = (sp: StoryPoints) => {
    const altText = `${mapStoryPoints(sp)}-icon`
    switch (sp) {
      case STORY_POINTS.UNESTIMATED:
        return <img src="/images/story-point-unestimated-icon.svg" alt={altText} />
      case STORY_POINTS.ZERO:
        return <div className="w-3.5 h-3.5" />
      case STORY_POINTS.ONE:
        return <img src="/images/story-point-1-icon.svg" alt={altText} />
      case STORY_POINTS.TWO:
        return <img src="/images/story-point-2-icon.svg" alt={altText} />
      case STORY_POINTS.THREE:
        return <img src="/images/story-point-3-icon.svg" alt={altText} />
    }
  }

  if (storyType === STORY_TYPES.RELEASE) return null

  return (
    <>
      <Label htmlFor={`estimate-${storyId}`} variant="title">
        Points
      </Label>
      <Select
        value={value.toString()}
        onValueChange={(newValue) => mutate({ estimate: Number.parseInt(newValue, 10) })}
        disabled={!isPointAssignable(storyType) || status === STORY_STATUSES.ACCEPTED}
      >
        <SelectTrigger
          id={`estimate-${storyId}`}
          className="w-25 p-0 flex gap-1 bg-surface-neutralMedium border-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 h-fit"
        >
          <SelectValue className="font-EN-KR-caption-regular text-contents-primary" placeholder="Story point" />
          <ChevronDown className="h-3.5 w-3.5" />
        </SelectTrigger>
        <SelectContent className="min-w-25 w-25 px-0 py-1 rounded-xs">
          {possiblePointsList.map(([_key, _value]) => (
            <SelectItem
              key={_key}
              value={_value.toString()}
              className="w-25 py-1.5 px-2 cursor-pointer relative hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8 after:absolute after:inset-0 rounded-none"
            >
              <div className="flex gap-1 items-center justify-start">
                {getIcon(_value)}
                <span className="font-EN-KR-caption-regular text-contents-primary">{mapStoryPoints(_value)}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  )
}

export const TypedEditableEstimate = EditableComponent as unknown as ForwardRefExoticComponent<
  EditableProps<number, ComponentProps<typeof Estimate>, ComponentProps<typeof EditableEstimate>> &
    RefAttributes<EditableCommands>
>
